import React, { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import api, { formatApiError } from "@/lib/api";
import { Building2, MapPin, Plus, Tablet, Edit3, Trash2, X, KeyRound, LogOutIcon, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function Sites() {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", address: "", postcode: "", country: "UK" });
  const [saving, setSaving] = useState(false);
  const [drawer, setDrawer] = useState(null);  // currently-edited site

  const load = async () => {
    try {
      const { data } = await api.get("/sites");
      setSites(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/sites", { ...form, opening_hours: { mon: "09:00-17:00" } });
      setForm({ name: "", address: "", postcode: "", country: "UK" });
      setShowForm(false);
      await load();
      toast.success("Site created");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete site '${s.name}'?`)) return;
    try {
      await api.delete(`/sites/${s.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <AppShell>
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="eb-overline mb-2">Locations</div>
          <h1 className="text-3xl font-semibold" data-testid="sites-heading">Sites</h1>
          <p className="text-sm text-navy-500 mt-1">Every physical location where visitors can sign in.</p>
        </div>
        <button className="btn-accent" onClick={() => setShowForm(!showForm)} data-testid="new-site-btn">
          <Plus className="w-4 h-4" /> New site
        </button>
      </div>

      {showForm && (
        <form onSubmit={create} className="eb-card p-6 mb-6 grid md:grid-cols-4 gap-3" data-testid="new-site-form">
          <input required placeholder="Site name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="eb-input md:col-span-1" data-testid="site-name-input" />
          <input required placeholder="Address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="eb-input md:col-span-1" data-testid="site-address-input" />
          <input required placeholder="Postcode" value={form.postcode} onChange={(e) => setForm({ ...form, postcode: e.target.value })} className="eb-input md:col-span-1" data-testid="site-postcode-input" />
          <select value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="eb-input md:col-span-1" data-testid="site-country-select">
            <option value="UK">United Kingdom</option>
            <option value="IE">Ireland</option>
          </select>
          <div className="md:col-span-4 flex justify-end gap-2">
            <button type="button" className="btn-ghost" onClick={() => setShowForm(false)}>Cancel</button>
            <button type="submit" disabled={saving} className="btn-accent" data-testid="site-create-submit">
              {saving ? "Creating…" : "Create site"}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <p className="text-navy-500 py-10 text-center">Loading…</p>
      ) : sites.length === 0 ? (
        <div className="eb-card p-10 text-center" data-testid="sites-empty">
          <Building2 className="w-10 h-10 mx-auto text-navy-300 mb-3" />
          <h2 className="font-semibold">No sites yet</h2>
          <p className="text-sm text-navy-500 mt-1 mb-5">Create your first site to get started.</p>
          <button className="btn-accent" onClick={() => setShowForm(true)} data-testid="sites-empty-cta">
            <Plus className="w-4 h-4" /> Create your first site
          </button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="sites-grid">
          {sites.map((s) => (
            <div key={s.id} className="eb-card p-5" data-testid={`site-card-${s.id}`}>
              <div className="flex items-start justify-between">
                <div className="w-10 h-10 rounded-lg bg-navy-50 text-navy-700 flex items-center justify-center">
                  <Building2 className="w-5 h-5" />
                </div>
                <span className="text-[10px] uppercase tracking-wider text-navy-400 font-semibold">{s.postcode}</span>
              </div>
              <h3 className="mt-3 font-semibold text-navy-900">{s.name}</h3>
              <div className="text-sm text-navy-500 mt-1 flex items-start gap-1.5">
                <MapPin className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" /> {s.address}
              </div>
              <div className="mt-4 flex gap-2">
                <button onClick={() => setDrawer(s)} className="btn-outline flex-1" data-testid={`site-edit-${s.id}`}>
                  <Edit3 className="w-4 h-4" /> Edit
                </button>
                <button onClick={() => remove(s)} className="btn-outline" data-testid={`site-delete-${s.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {drawer && <SiteEditDrawer site={drawer} onClose={() => setDrawer(null)} onSaved={load} />}
    </AppShell>
  );
}

function SiteEditDrawer({ site, onClose, onSaved }) {
  const [tab, setTab] = useState("details");
  const [form, setForm] = useState(site);
  const [kiosks, setKiosks] = useState([]);
  const [saving, setSaving] = useState(false);
  const [pin, setPin] = useState(null);

  const loadKiosks = async () => {
    try {
      const { data } = await api.get(`/kiosks?site_id=${site.id}`);
      setKiosks(data);
    } catch (e) { console.warn("[Sites] failed to load kiosks", e); }
  };
  useEffect(() => { loadKiosks(); }, [site.id]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name, address: form.address, address2: form.address2, city: form.city,
        postcode: form.postcode, country: form.country,
        opening_hours: form.opening_hours,
        contacts: form.contacts || [],
        idle_lock_seconds: Number(form.idle_lock_seconds || 90),
        auto_sign_out_at: form.auto_sign_out_at || "22:00",
      };
      await api.patch(`/sites/${site.id}`, payload);
      toast.success("Saved");
      onSaved();
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const newKiosk = async () => {
    try {
      const { data } = await api.post("/kiosks/pairing", { site_id: site.id, name: `Kiosk ${kiosks.length + 1}` });
      setPin({ pin: data.pairing_pin, kiosk_id: data.id });
      toast.success("Pairing PIN generated");
      loadKiosks();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const regen = async (kid) => {
    try {
      const { data } = await api.post(`/kiosks/${kid}/regenerate-pin`);
      setPin({ pin: data.pairing_pin, kiosk_id: kid });
      toast.success("PIN regenerated");
      loadKiosks();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const unpair = async (kid) => {
    if (!window.confirm("Unpair this kiosk? Its current token will be revoked.")) return;
    try { await api.post(`/kiosks/${kid}/unpair`); loadKiosks(); toast.success("Unpaired"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const bulkSO = async () => {
    if (!window.confirm("Bulk sign out everyone at this site? This will mark all signed-in visitors as out.")) return;
    try {
      const { data } = await api.post(`/sites/${site.id}/bulk-sign-out`, { confirm: true });
      toast.success(`Signed out ${data.signed_out_count} visitor(s)`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="fixed inset-0 bg-navy-900/40 backdrop-blur-sm z-50 flex justify-end" onClick={onClose} data-testid="site-drawer">
      <div className="w-full max-w-lg bg-white h-full overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-navy-100 p-5 flex items-center justify-between z-10">
          <div>
            <div className="eb-overline">Edit site</div>
            <div className="font-semibold text-lg">{site.name}</div>
          </div>
          <button onClick={onClose} className="btn-ghost" data-testid="site-drawer-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-5 pt-4 border-b border-navy-100 flex gap-1">
          {["details", "kiosks", "actions"].map((k) => (
            <button key={k} onClick={() => setTab(k)} className={`px-3 py-2 text-sm font-medium border-b-2 ${tab === k ? "border-orange-500 text-orange-600" : "border-transparent text-navy-600"}`} data-testid={`site-tab-${k}`}>
              {k === "details" ? "Details" : k === "kiosks" ? "Kiosks" : "Actions"}
            </button>
          ))}
        </div>

        <div className="p-5 space-y-4">
          {tab === "details" && (
            <>
              <Input label="Name" v={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="drawer-name" />
              <Input label="Address" v={form.address} onChange={(v) => setForm({ ...form, address: v })} testid="drawer-address" />
              <div className="grid grid-cols-2 gap-3">
                <Input label="Postcode" v={form.postcode} onChange={(v) => setForm({ ...form, postcode: v })} testid="drawer-postcode" />
                <Select label="Country" v={form.country} onChange={(v) => setForm({ ...form, country: v })} options={[{ v: "UK", l: "UK" }, { v: "IE", l: "Ireland" }]} testid="drawer-country" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Input label="Auto sign-out (HH:MM)" v={form.auto_sign_out_at || "22:00"} onChange={(v) => setForm({ ...form, auto_sign_out_at: v })} testid="drawer-auto" />
                <Input label="Idle lock (sec, 30–300)" type="number" v={form.idle_lock_seconds || 90} onChange={(v) => setForm({ ...form, idle_lock_seconds: v })} testid="drawer-idle" />
              </div>
              <button onClick={save} disabled={saving} className="btn-accent w-full mt-3" data-testid="drawer-save">{saving ? "Saving…" : "Save changes"}</button>
            </>
          )}
          {tab === "kiosks" && (
            <>
              <button onClick={newKiosk} className="btn-accent w-full" data-testid="drawer-new-kiosk"><Tablet className="w-4 h-4" /> New kiosk pairing PIN</button>
              {pin && (
                <div className="border-2 border-orange-500 rounded-xl p-5 bg-orange-50/40 text-center" data-testid="drawer-pin-box">
                  <div className="text-xs uppercase tracking-wider text-orange-600">Pairing PIN</div>
                  <div className="text-4xl font-bold tracking-[0.2em] mt-2">{pin.pin}</div>
                  <div className="text-xs text-navy-500 mt-2 break-all">Kiosk ID: <code>{pin.kiosk_id}</code></div>
                </div>
              )}
              <div className="space-y-2">
                {kiosks.map((k) => (
                  <div key={k.id} className="border border-navy-100 rounded-lg p-3" data-testid={`kiosk-row-${k.id}`}>
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-medium text-sm">{k.name}</div>
                        <div className="text-xs text-navy-500">{k.paired_at ? `Paired ${new Date(k.paired_at).toLocaleString()}` : "Unpaired"}</div>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => regen(k.id)} className="btn-ghost text-xs" data-testid={`kiosk-regen-${k.id}`}><KeyRound className="w-3 h-3" /></button>
                        {k.paired_at && (
                          <button onClick={() => unpair(k.id)} className="btn-ghost text-xs" data-testid={`kiosk-unpair-${k.id}`}><LogOutIcon className="w-3 h-3" /></button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                {kiosks.length === 0 && <p className="text-navy-500 text-sm">No kiosks yet.</p>}
              </div>
            </>
          )}
          {tab === "actions" && (
            <div className="space-y-3">
              <div className="rounded-lg border border-warning/40 bg-warning/10 p-4">
                <div className="flex items-center gap-2 text-sm font-semibold text-warning">
                  <AlertTriangle className="w-4 h-4" /> Bulk sign-out
                </div>
                <p className="text-xs text-navy-700 mt-2">Marks ALL currently signed-in visitors as signed out at this site. Audit-logged.</p>
                <button onClick={bulkSO} className="btn-outline mt-3" data-testid="bulk-signout-btn">
                  <LogOutIcon className="w-4 h-4" /> Bulk sign out everyone
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Input({ label, v, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="eb-label">{label}</label>
      <input type={type} value={v ?? ""} onChange={(e) => onChange(e.target.value)} className="eb-input" data-testid={testid} />
    </div>
  );
}
function Select({ label, v, onChange, options, testid }) {
  return (
    <div>
      <label className="eb-label">{label}</label>
      <select value={v} onChange={(e) => onChange(e.target.value)} className="eb-input" data-testid={testid}>
        {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </div>
  );
}
